import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { BRAND_NAME, BRAND_CODE, PRODUCT_SUBLABEL, BRAND_LOGO_SRC } from '../../config/branding';

const SIDEBAR_STATE_KEY = 'dd_spa.sidebarCollapsed';

interface NavItem {
  label: string;
  path: string;
  matchPrefixes: string[];           // exact prefix matches
  matchRoutePatterns?: RegExp[];     // catches /assessments/DD-…, /processing/DD-…, /reports/DD-…
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    matchPrefixes: ['/dashboard'],
    icon: (
      <svg className="sb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    /* Sprint 5 — Project-first audit listing. Discoverable next to
       the existing per-module nav items; the legacy /dd and /spa
       entries below stay for back-compat until the analyst team
       fully migrates. */
    label: 'Projects',
    path: '/projects',
    matchPrefixes: ['/projects'],
    icon: (
      <svg className="sb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7h18M3 12h18M3 17h18"/>
      </svg>
    ),
  },
  /* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #2) — Reports
     entry hidden from the sidebar because the unified Reports UI is
     not part of the current release. Analysts reach reports via the
     Projects page → Workspace → per-module cards. Keeping the entry
     commented out (not deleted) so we can re-enable it once the
     unified Reports landing ships. */
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Collapsed state lives in localStorage so the choice survives reload.
  // We toggle a class on <body> so .app-main can shift its margin via
  // CSS without lifting the state into App.tsx.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_STATE_KEY) === '1';
  });

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    window.localStorage.setItem(SIDEBAR_STATE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
    {/* Toggle is rendered OUTSIDE .app-sidebar so it isn't clipped by
        the sidebar's overflow-y:auto (which silently forces overflow-x
        to auto and chops the right-edge chevron). Pinned to the
        viewport at the sidebar's right edge; CSS shifts left when
        body.sidebar-collapsed is set. */}
    <button
      className="sb-toggle"
      type="button"
      onClick={() => setCollapsed((c) => !c)}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-pressed={collapsed}
      data-tip={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {collapsed
          ? <polyline points="9 18 15 12 9 6" />
          : <polyline points="15 18 9 12 15 6" />}
      </svg>
    </button>
    <nav
      className={`app-sidebar${collapsed ? ' collapsed' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className={`sb-brand${BRAND_LOGO_SRC ? ' has-image-logo' : ''}`}>
        {BRAND_LOGO_SRC ? (
          <img className="sb-logo-img" src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} />
        ) : (
          <div className="sb-logo">{BRAND_CODE}</div>
        )}
        <div className="sb-brand-info collapsed-hide">
          <span className="sb-brand-name">{BRAND_NAME}</span>
          <span className="sb-brand-sub">{PRODUCT_SUBLABEL}</span>
        </div>
      </div>

      <ul className="sb-nav">
        {NAV_ITEMS.map((item) => {
          const path = location.pathname;
          const isActive =
            item.matchPrefixes.some((p) =>
              p === '/dashboard' ? path === p : path === p || path.startsWith(p + '/'),
            ) ||
            (item.matchRoutePatterns?.some((re) => re.test(path)) ?? false);
          return (
            <li key={item.path}>
              <a
                href={item.path}
                className={`sb-item${isActive ? ' active' : ''}`}
                onClick={(e) => { e.preventDefault(); navigate(item.path); }}
                data-tip={collapsed ? item.label : undefined}
              >
                {item.icon}
                <span className="sb-item-label collapsed-hide">{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>

      <div className="sb-footer">
        {/* Sprint 5 — theme toggle wired to useTheme hook. */}
        <ThemeToggleButton collapsed={collapsed} />
        {/* Sprint 5 client feedback 2026-07-02 — Settings entry removed
            from the sidebar footer. It was a disabled placeholder link
            ("coming soon") that took navigation real estate without
            functioning; the surrounding footer flex layout tightens
            up naturally once the anchor is gone, and no other code
            references `sb-item Settings` so removal is layout-safe. */}
        {user && (
          <div className="sb-user">
            <div className="sb-avatar" data-tip={collapsed ? (user.full_name || user.email) : undefined}>
              {getInitials(user.full_name || user.email)}
            </div>
            <div className="sb-user-meta collapsed-hide">
              <span className="sb-user-name">{user.full_name || user.email}</span>
              <span className="sb-user-role">{user.role}</span>
            </div>
            <button
              className="sb-signout collapsed-hide"
              onClick={handleLogout}
              data-tip="Sign out"
              type="button"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        )}
      </div>
    </nav>
    </>
  );
}

/* Sprint 5 — sidebar theme toggle wired to useTheme hook (T113 + T122).
 * Sun icon when in dark mode (click = switch to light), moon icon when
 * in light mode (click = switch to dark). */
function ThemeToggleButton({ collapsed }: { collapsed: boolean }): JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      className="sb-item"
      type="button"
      onClick={toggleTheme}
      data-tip={collapsed ? (isDark ? 'Switch to light' : 'Switch to dark') : undefined}
    >
      {isDark ? (
        <svg
          className="sb-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg
          className="sb-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      )}
      <span className="sb-item-label collapsed-hide">
        {isDark ? 'Light Mode' : 'Dark Mode'}
      </span>
    </button>
  );
}
